'use client';
import { MouseEvent as ReactMouseEvent, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getDocumentById } from '@/api/documents';
import { checkCoupon, checkEmail } from '@/api/payments';
import Button from '@/components/elements/button';
import { Checkbox } from '@/components/elements/checkbox';
import Input from '@/components/elements/input';
import { useToggle } from '@/hooks/use-toggle';
import { useTranslation } from '@/i18n/client';
import { selectSelectedTickets } from '@/store/event-payment/event-payment.selectotrs';
import { Document } from '@/types/documents';
import { EventDetails } from '@/types/events';
import { CurrencyCode } from '@/types/general/base';
import { WithLocale } from '@/types/platform-data';
import { TicketsBookInput, TicketSelectOption } from '@/types/tickets';
import { documentIds, regexPaterns } from '@/utils/constants';
import { getDocUrl } from '@/utils/file-asset-utils';
import { priceToFixed } from '@/utils/number-utils';

interface Props {
    event: EventDetails;
    onSlideBack: () => void;
    onSubmit: (formData: TicketsBookInput) => Promise<void>;
    currencyCode: CurrencyCode;
    isVisible: boolean;
}

export default function SubmitForm(props: WithLocale<Props>) {
    const { t: tCommon } = useTranslation(props.lang, 'common');
    const { t: tPayments } = useTranslation(props.lang, 'payments');

    const selectedTickets = useSelector(selectSelectedTickets);

    const { watch, formState, handleSubmit, setError, clearErrors } = useFormContext<TicketsBookInput>();
    const { handleToggle, toggleValue } = useToggle(false);

    const [couponDiscount, setCouponDiscount] = useState<number | null>(0);
    const [loading, setLoading] = useState(false);
    const [document, setDocument] = useState<Document>();

    useEffect(() => {
        const fetchDocument = async () => {
            const doc = await getDocumentById(
                props.lang === 'ru' ? documentIds.terms_of_use_ru : documentIds.terms_of_use_sr,
            );
            if (doc) {
                setDocument(doc);
            }
        };

        fetchDocument();
    }, [props.lang]);

    const getTicketTranslation = (item: TicketSelectOption) => {
        const desc = item.description?.replace(/-/g, ' ').split(' ') ?? [];
        if (desc.length === 0) {
            return item.tag;
        }

        let translate = '';
        for (const word of desc) {
            const translationKey = `seat_schema.${word}`;
            // @ts-ignore
            const translation = tPayments(translationKey);
            translate += translation !== translationKey ? `${translation} ` : `${word} `;
        }
        return translate;
    };

    const order = selectedTickets.map((ticket) => ({
        tag: getTicketTranslation(ticket),
        quantity: ticket.quantity,
        price: priceToFixed(ticket.price * ticket.quantity * (couponDiscount ? 1 - couponDiscount : 1)) ?? 0,
    }));

    const total = priceToFixed(order.reduce((acc, ticket) => +acc + +ticket.price, 0));
    const submitBtnDisabled =
        !toggleValue ||
        watch(['customer_email', 'customer_email_repeat', 'customer_first_name', 'customer_second_name']).some(
            (item) => !item,
        ) ||
        Object.values(formState.errors).some((item) => item);

    const applyCoupon = async (e: ReactMouseEvent<Element>): Promise<void> => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await checkCoupon(props.event.id, watch('coupon_code') ?? '');
            setCouponDiscount(response.discount ? response.discount / 100 : null);
        } finally {
            setLoading(false);
        }
    };

    const validateEmail = async () => {
        const response = await checkEmail(encodeURI(watch('customer_email').toString()));
        if (!response) {
            setError('customer_email', { message: tPayments('form.errors.invalidEmail') });
        } else {
            clearErrors('customer_email');
        }
    };

    const compareEmail = () => {
        if (
            watch('customer_email').toString().toLocaleLowerCase() !==
            watch('customer_email_repeat').toString().toLocaleLowerCase()
        ) {
            setError('customer_email_repeat', { message: tPayments('form.errors.invalidEmailRepeat') });
        } else {
            clearErrors('customer_email_repeat');
        }
    };

    const onSubmit = async (formData: TicketsBookInput) => {
        // here the "cheaper" validation comes first
        // todo: cancel the submission if either of them failed
        compareEmail();
        await validateEmail();

        const country_code = props.currencyCode === 'RUB' ? 'ru' : 'rs';
        const payment_provider = country_code === 'rs' ? 'msu' : 'robokassa';
        const coupon_code = couponDiscount ? watch('coupon_code') : '';
        const ticketsBookInput = {
            ...formData,
            event_id: props.event.id,
            coupon_code,
            country_code,
            payment_provider,
            language: props.lang,
            body: selectedTickets.map((ticket) => ({ tag: ticket.tag, quantity: ticket.quantity })),
        };

        await props.onSubmit(ticketsBookInput);
    };

    return (
        <div className={`relative mt-10 min-w-full sm:mt-0 ${props.isVisible ? 'h-fit' : 'h-0'}`}>
            <form onSubmit={handleSubmit(onSubmit)} className="mx-auto flex w-full flex-col gap-y-1 px-2 md:w-1/2">
                <div className="flex flex-col gap-x-2 sm:flex-row">
                    <Input
                        name="customer_email"
                        label="Email"
                        placeholder="myemail@tutu.rs"
                        options={{
                            required: { value: true, message: tPayments('form.errors.requiredEmail') },
                            onBlur: async () => validateEmail(),
                            onChange: async (e) => {
                                e.target.value = e.target.value.trim().replace(regexPaterns.sanitizeEmail, '');
                                await validateEmail();
                            },
                        }}
                        withError={Boolean(formState?.errors?.customer_email)}
                        errText={formState?.errors?.customer_email?.message}
                    />
                    <Input
                        name="customer_email_repeat"
                        label="Repeat email"
                        options={{
                            required: { value: true, message: tPayments('form.errors.requiredEmailRepeat') },
                            onBlur: () => compareEmail(),
                            onChange: (e) => {
                                e.target.value = e.target.value.trim().replace(regexPaterns.sanitizeEmail, '');
                                compareEmail();
                            },
                        }}
                        onPaste={(e) => e.preventDefault()}
                        withError={Boolean(formState?.errors?.customer_email_repeat)}
                        errText={formState?.errors?.customer_email_repeat?.message}
                    />
                </div>
                <div className="flex flex-col gap-x-2 sm:flex-row">
                    <Input
                        label={tCommon('inputs.title.first_name')}
                        name="customer_first_name"
                        options={{
                            required: { value: true, message: tPayments('form.errors.requiredName') },
                            onBlur: (e) =>
                                e.target.value.length === 0
                                    ? setError('customer_first_name', {
                                          message: tPayments('form.errors.requiredName'),
                                      })
                                    : clearErrors('customer_first_name'),
                            onChange: (e) => {
                                e.target.value = e.target.value
                                    .trim()
                                    .replace(/[^A-Za-zА-я]/g, '')
                                    .slice(0, 34);
                                e.target.value.length === 0
                                    ? setError('customer_first_name', {
                                          message: tPayments('form.errors.requiredName'),
                                      })
                                    : clearErrors('customer_first_name');
                            },
                        }}
                        withError={Boolean(formState?.errors?.customer_first_name)}
                        errText={formState?.errors?.customer_first_name?.message}
                    />
                    <Input
                        label={tCommon('inputs.title.second_name')}
                        name="customer_second_name"
                        options={{
                            required: { value: true, message: tPayments('form.errors.requiredSecondName') },
                            onBlur: (e) =>
                                e.target.value.length === 0
                                    ? setError('customer_second_name', {
                                          message: tPayments('form.errors.requiredSecondName'),
                                      })
                                    : clearErrors('customer_second_name'),
                            onChange: (e) => {
                                e.target.value = e.target.value
                                    .trimStart()
                                    .replace(regexPaterns.sanitizeText, '')
                                    .slice(0, 34);
                                e.target.value.length === 0
                                    ? setError('customer_second_name', {
                                          message: tPayments('form.errors.requiredSecondName'),
                                      })
                                    : clearErrors('customer_second_name');
                            },
                        }}
                        withError={Boolean(formState?.errors?.customer_second_name)}
                        errText={formState?.errors?.customer_second_name?.message}
                    />
                </div>
                <Input
                    name="customer_address"
                    label={`${tCommon('inputs.title.address')} ${tCommon('inputs.title.optional')}`}
                    options={{
                        onChange: (e) =>
                            (e.target.value = e.target.value
                                .trimStart()
                                .replace(regexPaterns.sanitizeTextAndNumbers, '')),
                    }}
                    placeholder="Beograd, Kneza Miloša 27"
                />

                <Input
                    name="customer_phone"
                    label={`${tCommon('inputs.title.phone')} ${tCommon('inputs.title.optional')}`}
                    placeholder="+381123456789"
                    options={{
                        onChange: (e) =>
                            (e.target.value = e.target.value.replace(/(?!^\+)\+/g, '').replace(/[^\d+]/g, '')),
                        validate: (value) =>
                            value.length === 0 || regexPaterns.phone.test(value) || tCommon('inputs.validation.phone'),
                    }}
                    withError={Boolean(formState?.errors?.customer_phone)}
                />
                <Input
                    name="customer_telegram"
                    label={`Telegram ${tCommon('inputs.title.optional')}`}
                    options={{
                        onChange: (e) =>
                            (e.target.value = e.target.value.trim().replace(regexPaterns.sanitizeSocialLink, '')),
                    }}
                    placeholder="@polako"
                />
                {props.event.with_promocode && (
                    <div className="flex flex-col items-center gap-x-2 sm:flex-row">
                        <Input
                            withSuccess={Boolean(couponDiscount)}
                            name="coupon_code"
                            label={`${tPayments('common.promo_code')} ${tCommon('inputs.title.optional')}`}
                            withError={couponDiscount === null}
                            errText={'Неверный промокод'}
                        />
                        <Button
                            disabled={!Boolean(watch('coupon_code'))}
                            title={tCommon('common.submit')}
                            type="button"
                            onClick={applyCoupon}
                            loading={loading}
                        />
                    </div>
                )}
                {order && (
                    <div className="sm:text-md mx-auto mb-6 mt-4 flex w-full flex-col items-end gap-x-2 text-sm transition-transform duration-200 sm:mt-0">
                        <span>{tPayments('tickets.order')}</span>
                        {order?.map((ticket, index) => (
                            <span key={index}>
                                {tPayments('tickets.list', {
                                    tag: ticket.tag,
                                    quantity: ticket.quantity > 1 ? ticket.quantity : '',
                                    pcs: ticket.quantity > 1 ? tCommon('common.pcs') : '',
                                    price: ticket.price,
                                    currency: props.currencyCode,
                                })}
                            </span>
                        ))}
                        <span className="mt-4 font-bold">
                            {tPayments('tickets.total', {
                                returnObjects: true,
                                total: total,
                                currency: props.currencyCode,
                            })}
                        </span>
                        {couponDiscount !== null && couponDiscount > 0 && (
                            <span className="text-xs">
                                {tPayments('tickets.discount', { discount: couponDiscount * 100 })}
                            </span>
                        )}
                    </div>
                )}
                <Button
                    disabled={submitBtnDisabled}
                    type="submit"
                    title={tCommon('common.pay')}
                    className="mb-4"
                    onSubmit={handleSubmit(onSubmit)}
                    loading={formState.isSubmitting}
                />
                <Checkbox
                    id="agreement"
                    name="agreement"
                    label={tPayments('tickets.agreement', {
                        returnObjects: true,
                        link: document && getDocUrl(document),
                    })}
                    onChange={handleToggle}
                />
            </form>
        </div>
    );
}
